import type {
  Alert,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import {
  useAlerts,
  usePhones,
  useUpdatePhone,
  useCreatePhone,
  useUpdateAlert,
} from "src/hooks"
import {
  CreatePhoneErrorToast,
  SavePhoneSuccessToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

export function useOnSubmitPhone() {
  const { showToast } = useToasterContext()
  const { data: phones } = usePhones()
  const { data: alerts } = useAlerts()

  const updateAlert = useUpdateAlert()
  const updatePhone = useUpdatePhone()
  const createPhone = useCreatePhone()

  return useCallback(
    async (
      currentPhone: Phone,
      originalPhone: Phone,
      closeModal: () => void,
    ) => {
      if (currentPhone.phone_number === originalPhone.phone_number) {
        showToast(
          "save-phone-success-toast",
          SavePhoneSuccessToast,
        )

        closeModal()
        return
      }
      try {
        if (!updatePhone || !createPhone || !updateAlert) {

          throw new Error("cannot edit phone")
        }

        // this finds a phone that already matches the form's phone_number value
        const matchingPhone = phones?.find((phone) => phone.phone_number === currentPhone.phone_number)

        // this finds all alerts using the original phone number
        const alertsUsingPhone: Alert[] = (alerts ?? []).filter(
          ({ phone_ids }) => phone_ids.includes(originalPhone.id),
        )

        // demote the original phone from primary if it was saved before
        if (originalPhone.created_at) {
          await updatePhone({
            id: originalPhone.id,
            is_primary: false,
          })
        }

        // create a new primary phone while keeping the old one as a secondary phone
        const response = await (
          matchingPhone
            ? updatePhone({
              id: matchingPhone.id,
              is_primary: true,
            })
            : createPhone({
              phone_number: currentPhone.phone_number,
              is_primary: true,
            })
        )

        const newPhoneId = response.data.phone.id

        // update all alerts that were using the primary phone with the new primary phone
        await Promise.all(alertsUsingPhone.map(async (sugarmateAlert) => {
          // remove old phone id from phone_ids
          const phoneIds = sugarmateAlert.phone_ids.filter(
            (id: string) => id !== originalPhone.id,
          )

          // add new phone id to phone_ids
          phoneIds.push(newPhoneId)

          // update the alert
          await updateAlert({
            id: sugarmateAlert.id,
            phone_ids: phoneIds,
          })
        }))

        closeModal()

        showToast(
          "save-phone-success-toast",
          SavePhoneSuccessToast,
        )
      } catch (error) {
        const sugarmateError = getSugarmateError(error)

        const ErrorToast = injectProps(
          CreatePhoneErrorToast,
          {
            code: sugarmateError?.code,
            status: sugarmateError?.status,
          },
        )

        showToast(
          "save-phone-error-toast",
          ErrorToast,
        )
      }
    },
    [
      showToast,
      alerts,
      phones,
      updatePhone,
      createPhone,
      updateAlert,
    ],
  )
}
